<template>
  <div class="form--password-reset">
    <ObservedForm
      v-if="!sentDialog"
      :loading="loading"
      noerror
      @submit="submit"
    >
      <VContainer>
        <VRow no-gutters>
          <VCol
            cols="12"
            class="text-center mb-4"
          >
            <slot name="header">
              <h3 class="text-uppercase">
                {{ $t('password.label.requestReset') }}
              </h3>
              <p class="my-2">
                {{ $t('password.reset.subheader') }}
              </p>
            </slot>
          </VCol>
          <VCol cols="12">
            <TextFieldWithValidation
              v-model="username"
              :label="usernameLabel ? usernameLabel : $t('label.email')"
              vid="email"
              :rules="{ required: true, email: !isDemo }"
              name="email"
              data-testid="username"
              required
            />
          </VCol>
          <VCol cols="12">
            <SBtn
              id="reset-btn"
              :color="btnColor"
              action
              type="submit"
              block
            >
              {{ submitBtnText || $t('input.btn.reset') }}
            </SBtn>
          </VCol>
          <VCol
            v-if="closeLink"
            cols="12"
            class="text-center pt-6"
          >
            <SBtn
              class="font-weight-bold"
              link-medium
              @click="emits('dialog:closed')"
            >
              {{ $t('input.btn.cancel') }}
            </SBtn>
          </VCol>
          <slot name="footer" />
        </VRow>
      </VContainer>
    </ObservedForm>
    <SentPasswordDialog
      v-model:display="sentDialog"
      :dialog-mode="dialogMode"
      :email="username"
      @update:display="close"
    />
  </div>
</template>

<script lang="ts" setup>
import TextFieldWithValidation from '~/components/base/fields/TextFieldWithValidation.vue'
import SentPasswordDialog from '~/components/auth/SentPasswordDialog.vue'
import ObservedForm from '~/components/base/ObservedForm.vue'
import { useAuthStore } from '~/stores/auth'


const emits = defineEmits<{
  (e: 'dialog:closed'): void
}>()

interface Props {
 btnColor?: string
 dialogModeInitial?: string
 submitBtnText?: string
 usernameLabel?: string
 closeLink?: boolean
 isDemo?: boolean
 showSentDialog?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  btnColor: 'primary',
  dialogModeInitial: 'sent',
  submitBtnText: undefined,
  usernameLabel: undefined,
  closeLink: false,
  isDemo: false,
  showSentDialog: true
})

const sentDialog = ref(false)
const dialogMode = ref('')
const username = ref('')

onMounted(() => {
  dialogMode.value = props.dialogModeInitial
})

const { processAction, loading } = useAction()

const authStore = useAuthStore()
// ObservedForm also ignores submits while `loading`, but its validation is async, so
// two quick submits can both pass that check; this guard catches the second one.
const submit = () => {
  if (loading.value) {
    return
  }
  return processAction(async () => {
    await $api()(`account/password-reset/${encodeURIComponent(username.value)}`, { method: 'post' })
    if (props.showSentDialog) {
      sentDialog.value = true
    }
  })
}
const close = () => {
  emits('dialog:closed')
  authStore.updateAuthMenu(false)
}

</script>

<style lang="scss" scoped>
  .form--password-reset {
    min-width: 280px;
    max-width: 350px;
  }
</style>
